import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LiveEventsProvider } from './context/LiveEventsContext';
import { EntitlementsProvider } from './context/EntitlementsContext';
import { AppShell } from './components/layout/AppShell';
import { ErrorBoundary } from './components/ErrorBoundary';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import MembersPage from './pages/MembersPage';
import EventsPage from './pages/EventsPage';
import AnnouncementsPage from './pages/AnnouncementsPage';
import PostsPage from './pages/PostsPage';
import MinistriesPage from './pages/MinistriesPage';
import CoursesPage from './pages/CoursesPage';
import ContributionsPage from './pages/ContributionsPage';
import PrayersPage from './pages/PrayersPage';
import PetitionsPage from './pages/PetitionsPage';
import SettingsPage from './pages/SettingsPage';
import SubscriptionsPage from './pages/SubscriptionsPage';
import BillingPage from './pages/BillingPage';
import CollectionsPage from './pages/CollectionsPage';
import SupportPage from './pages/SupportPage';
import HelpCenterPage from './pages/HelpCenterPage';
import ComunidadPage from './pages/ComunidadPage';
import ContenidoPage from './pages/ContenidoPage';
import AnalyticsPage from './pages/AnalyticsPage';
import TeamPage from './pages/TeamPage';
import GroupsPage from './pages/GroupsPage';
import WalletPage from './pages/WalletPage';
import MentorshipsPage from './pages/MentorshipsPage';
import ModerationPage from './pages/ModerationPage';
import ApoyoPastoralPage from './pages/ApoyoPastoralPage';
import './App.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false },
  },
});

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <img
            src="https://customer-assets.emergentagent.com/job_pastor-dashboard-2/artifacts/pvtvhzy7_LOGO%20Iglesia%201.png"
            alt="Tu Iglesia App"
            className="h-16 w-auto mx-auto mb-4 opacity-50"
          />
          <div className="w-8 h-8 border-2 border-[#056A84] border-t-transparent rounded-full spinner mx-auto" />
        </div>
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return null;
  if (isAuthenticated) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <EntitlementsProvider>
        <LiveEventsProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route
              element={
                <ProtectedRoute>
                  <AppShell />
                </ProtectedRoute>
              }
            >
              <Route path="/" element={<DashboardPage />} />
              <Route path="/members" element={<MembersPage />} />
              <Route path="/events" element={<EventsPage />} />
              <Route path="/announcements" element={<AnnouncementsPage />} />
              <Route path="/posts" element={<PostsPage />} />
              <Route path="/ministries" element={<MinistriesPage />} />
              <Route path="/groups" element={<GroupsPage />} />
              <Route path="/courses" element={<CoursesPage />} />
              <Route path="/contributions" element={<ContributionsPage />} />
              <Route path="/wallet" element={<WalletPage />} />
              <Route path="/prayers" element={<PrayersPage />} />
              <Route path="/petitions" element={<PetitionsPage />} />
              <Route path="/mentorships" element={<MentorshipsPage />} />
              <Route path="/moderation" element={<ModerationPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/subscriptions" element={<SubscriptionsPage />} />
              <Route path="/billing" element={<BillingPage />} />
              <Route path="/collections" element={<CollectionsPage />} />
              <Route path="/support" element={<SupportPage />} />
              <Route path="/help" element={<HelpCenterPage />} />
              <Route path="/comunidad" element={<ComunidadPage />} />
              <Route path="/comunidad/contenido" element={<ContenidoPage />} />
              <Route path="/analytics" element={<AnalyticsPage />} />
              <Route path="/team" element={<TeamPage />} />
              <Route path="/apoyo-pastoral" element={<ApoyoPastoralPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
        </LiveEventsProvider>
        </EntitlementsProvider>
      </AuthProvider>
    </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
